import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';
import { User } from '../models/user';
import { GlobalVariable } from '../helpers/settings';
import { Router } from '@angular/router';
import { SocialAuthService, SocialUser } from '@abacritt/angularx-social-login';
import { decrypt, encrypt } from '../helpers/crypto-js';
import { catchError } from 'rxjs/operators';
import { AppConfigService } from './app-config.service';

export interface PaginationData {
	docs: Partial<User>[];
	total: number;
	limit: number;
	page: number;
	pages: number;
}

@Injectable({
	providedIn: 'root'
})
export class AuthService {
	readonly BASE_URL = `${GlobalVariable.server.host}/auth`;
	userSubject: BehaviorSubject<User | null>;
	public user: Observable<User>;
	public authObject: any;

	constructor(
		private socialAuthService: SocialAuthService,
		private httpClient: HttpClient,
		private router: Router,
		private appConfig: AppConfigService
	) {
		const auth = this.getAuth();
		this.userSubject = new BehaviorSubject(auth?.user!);
		this.user = this.userSubject.asObservable();
		this.socialAuthService.authState.subscribe({
			next: user => {
				if (user) {
					this.signInByGoogle(user);
				}
			}
		});
	}

	login(user: User): Observable<any> {
		return this.httpClient.post<any>(`${this.BASE_URL}/login`, user).pipe(
			switchMap(auth => {
				if (auth.result.status == 401) return of(auth.result);
				this.setAuthAtLocalStorage(auth.result);
				return of({ user: this.userSubject.value, project: auth.result.project });
			})
		);
	}

	getAuth(): any {
		const auth = localStorage.getItem('auth');
		let authObject = JSON.parse(auth);
		if (authObject) {
			authObject.user = JSON.parse(decrypt(authObject.user));
			return authObject;
		}
	}

	logout() {
		// remove user from local storage to log user out
		localStorage.removeItem('auth');
		localStorage.removeItem('project');
		this.userSubject.next(null);
		this.appConfig.resetConfig();
		this.router.navigate(['auth/login']);
	}

	public get userValue() {
		return this.userSubject.value;
	}
	public get userValueWER() {
		return this.userSubject.value;
	}

	getCurrentUser(): User {
		const auth = localStorage.getItem('auth');
		if (!auth) return;
		let authObject = JSON.parse(auth);
		authObject.user = JSON.parse(decrypt(authObject?.user));
		return authObject?.user;
	}

	register(user: User, applications: string[]): Observable<{ status: number; message: string }> {
		return this.httpClient.post<any>(`${this.BASE_URL}/register`, { user, applications }).pipe(
			switchMap(res => {
				return of(res.result);
			}),
			catchError(error => {
				return of({ status: 0, message: 'Registration failed. Please try again.' }); // Message d'erreur générique
			})
		);
	}

	public reload = (res: any) => {
		if (res?.refresh) {
			window.location.reload();
		}
		return of(res.result);
	};

	private signInByGoogle(user: SocialUser) {
		this.httpClient.post(`${this.BASE_URL}/google`, user).subscribe(
			(response: any) => {
				const auth = {
					authToken: response?.result?.authToken,
					userInfo: response?.result?.user,
					user: response?.result?.user
				};
				this.setAuthAtLocalStorage(auth);
				this.router.navigate(['/']);
			},
			error => {}
		);
	}

	public setAuthAtLocalStorage(auth: any) {
		this.userSubject.next(auth.user);
		auth.user = encrypt(JSON.stringify(auth?.user));
		localStorage.setItem('auth', JSON.stringify(auth));
	}
}
